import { useFormId } from '@unistocks/components/form/components/form-id-context';
import { useExpressionValue } from '@unistocks/components/form/utils';
import { Column, Row } from '@unistocks/components/layout/flex';
import { getComponentDisplayName } from '@unistocks/shared/utils/get-display-name';
import { PropsWithRef } from '@unistocks/types';
import React from 'react';
import { ControllerFieldState, ControllerRenderProps, useController } from 'react-hook-form';
import { AnimatePresence, TamaguiElement, ViewProps } from 'tamagui';
import { FormComponentPropsBase } from '../model';
import { HelpText } from './help-text';
import { Label } from './label';
import { ValidationMessage } from './validation-message';

export type FormFieldInjectedProps = {
  /** react-hook-form controller binding for the field */
  field: ControllerRenderProps<any, any>;
  /** State of the field (invalid, error, ...) */
  fieldState: ControllerFieldState;
  /** Evaluated required state, already resolved from the `isRequired` expression */
  isRequired: boolean;
  /** Evaluated disabled state, includes the form level disabled flag */
  isDisabled: boolean;
};

export function withLabelAndValidation<
  TRef = TamaguiElement,
  TProps extends FormComponentPropsBase = FormComponentPropsBase
>(WrappedComponent: React.ComponentType<PropsWithRef<TProps & FormFieldInjectedProps, TRef>>) {
  const WithLabelAndValidation = (props: TProps) => {
    const {
      name,
      label,
      helpText,
      isRequired: requiredExpression,
      isDisabled: disabledExpression,
      hidden: hiddenExpression,
      dependsOn
    } = props;
    const formId = useFormId();
    const componentId = formId ? `${formId}_${name}` : `${name}`;
    const { field, fieldState } = useController({ name });

    const isHidden = useExpressionValue(hiddenExpression, dependsOn);
    const isRequired = useExpressionValue(requiredExpression, dependsOn);
    const isDisabled = useExpressionValue(disabledExpression, dependsOn) || !!field.disabled;

    return (
      /* TODO: How to disable initial animation? initial prop has no effect here */
      <AnimatePresence initial={false}>
        {isHidden ? null : (
          <Column
            key={`animate-${name}`}
            transition={'200ms'}
            enterStyle={{ opacity: 0, y: -10 }}
            exitStyle={{ opacity: 0, y: -30 }}
            gap={'$1'}
            mb={'$4'}
          >
            {label ? (
              <Row justifyContent={'space-between'} mb={'$0.5'}>
                <Label form htmlFor={componentId} invalid={fieldState.invalid}>
                  {label} {isRequired ? '*' : null}
                </Label>

                {helpText ? <HelpText text={helpText} title={label} /> : null}
              </Row>
            ) : null}

            <WrappedComponent
              {...props}
              id={componentId}
              field={field}
              fieldState={fieldState}
              isRequired={isRequired}
              isDisabled={isDisabled}
            />

            {fieldState.invalid && fieldState.error?.message ? (
              <ValidationMessage>{fieldState.error.message}</ValidationMessage>
            ) : null}
          </Column>
        )}
      </AnimatePresence>
    );
  };

  WithLabelAndValidation.displayName = `withLabelAndValidation(${getComponentDisplayName(
    WrappedComponent
  )})`;

  return WithLabelAndValidation;
}

export function withValidation<TProps extends FormComponentPropsBase, TRef = TamaguiElement>(
  WrappedComponent: React.ComponentType<PropsWithRef<TProps & FormFieldInjectedProps, TRef>>,
  containerProps?: ViewProps
) {
  const WithValidation = (props: TProps) => {
    const {
      name,
      hidden: hiddenExpression,
      isDisabled: disabledExpression,
      dependsOn
    } = props;
    const formId = useFormId();
    const componentId = formId ? `${formId}_${name}` : `${name}`;
    const { field, fieldState } = useController({ name });

    const isHidden = useExpressionValue(hiddenExpression, dependsOn);
    const isDisabled = useExpressionValue(disabledExpression, dependsOn) || !!field.disabled;
    const isRequired = useExpressionValue(props.isRequired, dependsOn);

    return (
      /* TODO: How to disable initial animation? initial prop has no effect here */
      <AnimatePresence initial={false}>
        {isHidden ? null : (
          <Column
            key={`animate-${name}`}
            transition={'200ms'}
            enterStyle={{ opacity: 0, y: -10 }}
            exitStyle={{ opacity: 0, y: -30 }}
            mb={'$4'}
            {...containerProps}
          >
            <WrappedComponent
              {...props}
              id={componentId}
              field={field}
              fieldState={fieldState}
              isRequired={isRequired}
              isDisabled={isDisabled}
            />

            {fieldState.invalid && fieldState.error?.message ? (
              <ValidationMessage>{fieldState.error.message}</ValidationMessage>
            ) : null}
          </Column>
        )}
      </AnimatePresence>
    );
  };

  WithValidation.displayName = `withValidation(${getComponentDisplayName(WrappedComponent)})`;

  return WithValidation;
}
